import {
  CAlert,
  CButton,
  CButtonGroup,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSelect,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getList } from "../../api/generics";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import TreasuryAccount, { TreasuryMovement } from "../../models/treasury";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, warningAlert } from "../utils/messages";

interface TreasuryAssignModalProps {
  show: boolean;
  movementsCount: number;
  totalAmount: number;
  suggestedAccountId?: number;
  // Reversiones incluidas en la asignacion: se avisa si van a una cuenta
  // distinta de la del movimiento original (su cuenta sugerida).
  reversals?: TreasuryMovement[];
  // En lote: agrega la opcion de mandar cada movimiento a su cuenta destino
  // (onConfirm recibe undefined) y cuantos no tienen destino.
  allowOwnDestination?: boolean;
  missingDestinationCount?: number;
  onCancel: () => void;
  onConfirm: (accountId: number | undefined) => Promise<void>;
}

const OWN_DESTINATION = "own";

const TreasuryAssignModal: React.FC<TreasuryAssignModalProps> = ({
  show,
  movementsCount,
  totalAmount,
  suggestedAccountId,
  reversals = [],
  allowOwnDestination = false,
  missingDestinationCount = 0,
  onCancel,
  onConfirm,
}) => {
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [accountId, setAccountId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAccounts = async () => {
    const accountsStatus = await getList<TreasuryAccount>(
      "/treasury/accounts/",
      -1,
      0,
      new Map([
        ["company_id", companyId?.toString()],
        ["active", "true"],
      ])
    );
    if (accountsStatus.status === SUCCESS && accountsStatus.data) {
      setAccounts(accountsStatus.data.items);
    } else {
      warningAlert(accountsStatus.detail ?? "Error desconocido");
    }
  };

  useEffect(() => {
    if (!show) {
      return;
    }
    setAccountId(
      allowOwnDestination
        ? OWN_DESTINATION
        : suggestedAccountId
        ? suggestedAccountId.toString()
        : ""
    );
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, suggestedAccountId, allowOwnDestination]);

  const onSave = async () => {
    if (accountId === "") {
      errorAlert("Seleccione una cuenta");
      return;
    }
    if (accountId === OWN_DESTINATION && missingDestinationCount > 0) {
      errorAlert(
        "Hay movimientos sin cuenta destino: elija una cuenta para todos"
      );
      return;
    }
    setSubmitting(true);
    await onConfirm(
      accountId === OWN_DESTINATION ? undefined : Number(accountId)
    );
    setSubmitting(false);
  };

  const mismatchedReversals =
    accountId === "" || accountId === OWN_DESTINATION
      ? []
      : reversals.filter(
          (reversal) =>
            reversal.suggestedAccountId !== undefined &&
            String(reversal.suggestedAccountId) !== accountId
        );

  return (
    <CModal show={show} onClose={onCancel}>
      <CModalHeader closeButton>
        <CModalTitle>Aceptar en cuenta</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p>
          {movementsCount === 1
            ? "1 movimiento"
            : `${movementsCount} movimientos`}{" "}
          por un total de <b>{formatToCurrency(totalAmount, PYG)}</b>
        </p>
        <CLabel>Cuenta:</CLabel>
        <CSelect
          value={accountId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setAccountId(e.target.value)
          }
        >
          <option value="" disabled>
            -----
          </option>
          {allowOwnDestination ? (
            <option value={OWN_DESTINATION}>
              Cuenta destino de cada movimiento
            </option>
          ) : (
            <></>
          )}
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
              {account.id === suggestedAccountId ? " (destino)" : ""}
            </option>
          ))}
        </CSelect>
        {allowOwnDestination ? (
          <small className="text-muted d-block mt-2">
            Los que ya están aceptados en la cuenta elegida se dejan como
            están.
          </small>
        ) : (
          <></>
        )}
        {accountId === OWN_DESTINATION && missingDestinationCount > 0 ? (
          <CAlert color="danger" className="mt-3 mb-0">
            {missingDestinationCount === 1
              ? "1 movimiento no tiene cuenta destino"
              : `${missingDestinationCount} movimientos no tienen cuenta destino`}
            : elija una cuenta para todos.
          </CAlert>
        ) : (
          <></>
        )}
        {mismatchedReversals.length === 1 ? (
          <CAlert color="warning" className="mt-3 mb-0">
            El movimiento original está en{" "}
            <b>{mismatchedReversals[0].suggestedAccountName}</b>; la reversión
            normalmente va a la misma cuenta.
          </CAlert>
        ) : mismatchedReversals.length > 1 ? (
          <CAlert color="warning" className="mt-3 mb-0">
            {mismatchedReversals.length} reversiones van a una cuenta distinta
            de la de su movimiento original; normalmente van a la misma
            cuenta.
          </CAlert>
        ) : (
          <></>
        )}
      </CModalBody>
      <CModalFooter>
        <CButtonGroup>
          <CButton color="secondary" onClick={onCancel}>
            Cancelar
          </CButton>
          <CButton color="primary" onClick={onSave} disabled={submitting}>
            {submitting ? "Aceptando..." : "Aceptar"}
          </CButton>
        </CButtonGroup>
      </CModalFooter>
    </CModal>
  );
};

export default TreasuryAssignModal;
